/* eslint-disable react/prop-types */
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getRouteMeta } from "../utils/seo/routeMetadata";
import {
  SITE_NAME,
  LOCALE,
  TWITTER_HANDLE,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_OG_IMAGE,
  toCanonicalUrl,
  toAbsoluteImage,
} from "../utils/seo/seoConfig";

/**
 * Per-page SEO head manager (react-helmet-async).
 *
 * Backward compatible with the original props (title, description, name, type,
 * pageLink, keywords) so the ~31 existing call sites keep working untouched —
 * they simply inherit the fixes below.
 *
 * Fixes vs the previous version:
 *  - twitter:card is now the VALID "summary_large_image" (was set to a slogan).
 *  - og:type is "website" (was a slogan).
 *  - adds og:image / og:url / og:site_name / og:locale + twitter:image.
 *  - canonical + og:url are ALWAYS re-pinned to https://www.jaaga.ai via
 *    toCanonicalUrl(), so a dev/staging origin can never leak into the tags.
 *  - removed the duplicate <link rel="canonical"> and the per-mount gtag
 *    re-injection (GA/GTM already load once in index.html).
 *
 * CURATED ROUTE METADATA (see ../utils/seo/routeMetadata.js)
 * For the 252 public URLs in the sitemap, the title / description / canonical /
 * keywords come from seo-metadata.json keyed on the CURRENT PATHNAME, and take
 * precedence over whatever the caller passed. That is the point: the props on
 * those pages are built from live API data, which is why /karnataka/rtc-pahani
 * could render the right <h1> and still report `canonical:
 * https://www.jaaga.ai/` — the file can't be slow, partial or undefined, and it
 * is verified to match public/sitemap.xml exactly, so a canonical and its
 * sitemap entry can never disagree.
 *
 * Any path NOT in that file (/intelligence/atlas/v/:village, /properties/*,
 * /dashboard, …) is untouched and keeps using the props below. `noindex`,
 * `jsonLd` and `image` are ALWAYS the caller's — a page that knows it is a
 * not-found/thin shell must stay able to say so. Pass `ignoreRouteMeta` to opt
 * a single render out entirely.
 *
 * New optional props:
 *  - image:   per-page OG/Twitter image (path or absolute URL).
 *  - noindex: emit <meta name="robots" content="noindex,follow"> for private pages.
 *  - jsonLd:  object OR array of schema.org objects -> <script type=ld+json>.
 *  - ogType:  override og:type (default "website"; e.g. "article").
 */
// Callers sometimes interpolate unresolved values into template strings, which
// arrives here as the literal text "undefined"/"null". Treat those (and any
// leading/trailing junk around them) as empty so they can never reach the head.
const sanitize = (value) => {
  const s = (value ?? "").toString().trim();
  if (!s) return "";
  // Drop values that are only "undefined"/"null" fragments and separators.
  if (/^(undefined|null|[\s|,\-–—:]|)+$/i.test(s)) return "";
  // Strip embedded "undefined"/"null" tokens (e.g. "EC in undefined").
  return s.replace(/\b(undefined|null)\b/gi, "").replace(/\s{2,}/g, " ").trim();
};

const SEO = ({
  title,
  description,
  name,
  // `type` is still accepted from existing callers (passed as a prop) but is
  // intentionally ignored — it used to (incorrectly) drive og:type/twitter:card.
  pageLink,
  keywords,
  image,
  noindex = false,
  jsonLd,
  ogType = "website",
  ignoreRouteMeta = false,
}) => {
  // Ground truth for the lookup is the URL actually being rendered, not the
  // caller's `pageLink` — `pageLink` being wrong/missing is the bug this fixes.
  const { pathname } = useLocation();
  const routeMeta = ignoreRouteMeta ? null : getRouteMeta(pathname);

  // seo-metadata.json stores keywords as an array; callers pass a string.
  const routeKeywords = Array.isArray(routeMeta?.keywords)
    ? routeMeta.keywords.join(", ")
    : routeMeta?.keywords;

  const pageTitle =
    sanitize(routeMeta?.title) || sanitize(title) || DEFAULT_TITLE;

  const pageDescription =
    sanitize(routeMeta?.description) ||
    sanitize(description) ||
    DEFAULT_DESCRIPTION;

  const pageKeywords =
    sanitize(routeKeywords) || sanitize(keywords) || DEFAULT_KEYWORDS;

  // twitter:creator must be a real @handle — old callers pass long site
  // descriptions via `name`, which is invalid; fall back to the site handle.
  const twitterCreator =
    typeof name === "string" && /^@[A-Za-z0-9_]{1,15}$/.test(name.trim())
      ? name.trim()
      : TWITTER_HANDLE;

  // Curated canonicals are already absolute, https, www and trailing-slash-free
  // (byte-identical to the sitemap), so they are used verbatim rather than
  // re-derived; everything else still goes through the domain-pinning helper.
  const canonical = routeMeta?.canonical || toCanonicalUrl(pageLink);
  const ogImage = image ? toAbsoluteImage(image) : DEFAULT_OG_IMAGE;

  const schemas = (
    jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []
  ).filter(Boolean);

  return (
    <Helmet prioritizeSeoTags>
      {/* Standard metadata */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? "noindex,follow" : "index,follow"}
      />

      {/* Open Graph (Facebook / WhatsApp / LinkedIn) */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALE} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={pageTitle} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={twitterCreator} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data (optional, per-page) */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
